// @flow

import * as React from 'react';
import { ButtonGroup, Button } from '@material-ui/core';
import { useLocale } from '../../../hooks/locale';
import { useTheme } from '../../../theme';
import { createDescriptor } from '../../../controls/router';
import type { AggregatesListingsFilters_root } from '../__generated__/AggregatesListingsFilters_root.graphql.js';
import { toggleCompositeTypeOfferType } from './propertyTypeFilterChoices';
import {
  offerTypeDescriptor,
  salePricePerSurfaceDescriptor,
  rentPricePerAdditionalDescriptor,
  pageDescriptor,
  sortDescriptor,
  grossRentDescriptor,
  salePriceDescriptor,
  propertyTypeFilterDescriptor,
  offerTypeFilterDescriptor,
} from './sharedDescriptors';

const combinedDescriptor = createDescriptor(
  offerTypeFilterDescriptor,
  pageDescriptor,
  propertyTypeFilterDescriptor,
);

type Props = {|
  root: AggregatesListingsFilters_root,
  inline?: boolean,
  shouldFlush: boolean,
|};

// Material-ui ButtonGroup clones Button elements replacing all props with it's own
// here is the hack to allow button to have it's own variant and color
const WrapButton = ({ wvariant, wcolor, ...props }) => {
  return <Button {...props} variant={wvariant} color={wcolor} />;
};

export const OfferTypeFilter = (props: Props): React.Node => {
  const { colors, text } = useTheme();
  const [, setSort] = sortDescriptor.useState();
  const [{ offerType }] = offerTypeDescriptor.useState();
  const [{ compositePropertyType_eq }, setParams, flushParams] =
    combinedDescriptor.useState();
  const { t } = useLocale();

  const flush = value => {
    setParams({
      page: 0,
      compositePropertyType_eq:
        value !== offerType
          ? toggleCompositeTypeOfferType(compositePropertyType_eq)
          : compositePropertyType_eq,
    });
    if (props.shouldFlush) {
      flushParams();
      window.scrollTo({ top: 0 });
    }
  };

  const options = [
    { value: 'buy', label: t('buy') },
    { value: 'rent', label: t('rentVerb') },
  ];

  const handleItemClick = value => {
    if (value === 'buy') {
      // reset renting filters for buy
      setParams(grossRentDescriptor.defaults);

      setParams(rentPricePerAdditionalDescriptor.defaults);

      setParams({
        offerType: value,
      });
    } else {
      // reset sale filters for buy
      setParams(salePriceDescriptor.defaults);
      setParams(salePricePerSurfaceDescriptor.defaults);

      setParams({
        offerType: value,
      });
    }

    setSort({
      sortBy: null,
      sortDirection: null,
    });

    flush(value);
  };

  if (props.inline === true) {
    return (
      <ButtonGroup
        css={{
          // flex 1 etc on childrens doesnt force elements to have
          // same size without knowing initial width. Grid does that easily
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
        }}
        fullWidth={true}
        size="medium"
      >
        {options.map(option => (
          <WrapButton
            key={option.value}
            css={{
              padding: '8px',
              fontWeight: text.font.medium,
              '&.MuiButton-contained': {
                // adding this border fixes height jumps during the switch
                border: '1px solid rgba(0, 0, 0, 0)',
              },
              '&.MuiButton-outlined': {
                // adding this border fixes height jumps during the switch
                border: '1px solid rgba(0, 0, 0, 0.23)',
              },
            }}
            wvariant={option.value === offerType ? 'contained' : 'outlined'}
            wcolor={option.value === offerType ? 'primary' : 'inherit'}
            onClick={() => handleItemClick(option.value)}
          >
            {option.label}
          </WrapButton>
        ))}
      </ButtonGroup>
    );
  } else {
    return (
      <ButtonGroup size="small">
        {options.map(option => (
          <WrapButton
            key={option.value}
            css={[
              text.body1,
              {
                padding: '4px 16px',
                borderRadius: '100px',
                cursor: 'pointer',
                fontWeight: text.font.regular,
              },
              option.value === offerType
                ? {
                    backgroundColor: colors.blue50,
                    color: colors.black,
                    borderColor: colors.blue100,
                  }
                : {
                    borderColor: colors.grey300,
                  },
            ]}
            wvariant="outlined"
            onClick={() => handleItemClick(option.value)}
            wcolor={'inherit'}
          >
            {option.label}
          </WrapButton>
        ))}
      </ButtonGroup>
    );
  }
};
